import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Plus, Trash2, ChevronRight, ClipboardList,
  Settings, Truck, Package, Tag, Layers, AlertCircle, Loader2,
  CheckCircle, Clock, ChefHat, Send, Receipt, FileText, X,
  ShoppingBag, Save, Edit2, RefreshCw, Printer, Factory,
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import FacturaFormModal from "../components/FacturaFormModal";
import ProductoSearchSelect from "../components/ProductoSearchSelect";
import { EntidadModal } from "./CatastroPage";
import { useSSE } from "../hooks/useSSE";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtGs = (n) =>
  `₲ ${Math.round(Number(n || 0)).toLocaleString("es-PY")}`;

const slugId = (str) =>
  str.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");

const canProd = (hasPermission, moderno, legacy = null) =>
  hasPermission(moderno) || (legacy ? hasPermission(legacy) : false);

// ── Colores de estado KDS ────────────────────────────────────────────────────
const KDS_ESTADOS = ["A RECIBIR", "EN PREPARACION", "LISTO", "DESPACHADO"];
const KDS_COLOR = {
  "A RECIBIR":     { bg: "bg-slate-700/60",  badge: "bg-slate-600 text-white",    icon: <Clock className="w-4 h-4" /> },
  "EN PREPARACION":{ bg: "bg-amber-900/40",  badge: "bg-amber-600 text-white",    icon: <ChefHat className="w-4 h-4" /> },
  "LISTO":         { bg: "bg-emerald-900/40",badge: "bg-emerald-600 text-white",  icon: <CheckCircle className="w-4 h-4" /> },
  "DESPACHADO":    { bg: "bg-blue-900/40",   badge: "bg-blue-600 text-white",     icon: <Send className="w-4 h-4" /> },
};
const KDS_CLASS = {
  "A RECIBIR": "a-recibir",
  "EN PREPARACION": "en-preparacion",
  "LISTO": "listo",
  "DESPACHADO": "despachado",
};

// Permiso granular por etapa: cada columna del Kanban se puede habilitar por
// separado (ver esa columna + avanzar los pedidos que están en ella), para no
// tener que mostrarle todo el proceso a todo el personal (ej: cocina no
// necesita ver Despacho, y viceversa).
const ETAPA_PERM = {
  "A RECIBIR": "produccion_kds.etapa_a_recibir",
  "EN PREPARACION": "produccion_kds.etapa_en_preparacion",
  "LISTO": "produccion_kds.etapa_listo",
  "DESPACHADO": "produccion_kds.etapa_despachado",
};

// Si el usuario no tiene NINGÚN permiso de etapa específico, se comporta como
// antes: ve y avanza las 4 columnas con "ver"/"avanzar" generales. En cuanto
// se le asigna al menos un permiso de etapa, queda restringido solo a esas.
const etapasVisibles = (hasPermission) => {
  const tieneAlgunaEspecifica = Object.values(ETAPA_PERM).some(p => hasPermission(p));
  if (!tieneAlgunaEspecifica) return KDS_ESTADOS;
  return KDS_ESTADOS.filter(estado => hasPermission(ETAPA_PERM[estado]));
};

// ── Mini-modal selector boleta / factura legal ───────────────────────────────
function TipoComprobantePicker({ orden, onSelect, onClose, hasPermission }) {
  const canBoleta = canProd(hasPermission, "produccion_facturacion.boleta", "produccion.factura_boleta");
  const canLegal  = canProd(hasPermission, "produccion_facturacion.factura_legal", "produccion.factura_legal");

  if (!canBoleta && !canLegal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-slate-800 rounded-xl p-6 w-80 text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-slate-300">Sin permiso para emitir comprobantes.</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white text-sm">Cerrar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 w-96 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">Emitir comprobante</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-5">
          Orden #{orden.nro_orden} — {orden.cliente?.nombre || "Sin cliente"}<br />
          <span className="text-emerald-400 font-semibold">{fmtGs(orden.totales?.total_general)}</span>
        </p>
        <div className="flex flex-col gap-3">
          {canBoleta && (
            <button
              onClick={() => onSelect("boleta")}
              className="flex items-center gap-3 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm transition-colors"
            >
              <Receipt className="w-5 h-5 text-amber-400" />
              <div className="text-left">
                <div className="font-medium">Boleta / Sin comprobante fiscal</div>
                <div className="text-slate-400 text-xs">Venta sin factura legal</div>
              </div>
            </button>
          )}
          {canLegal && (
            <button
              onClick={() => onSelect("factura_legal")}
              className="flex items-center gap-3 px-4 py-3 bg-indigo-700 hover:bg-indigo-600 rounded-lg text-white text-sm transition-colors"
            >
              <FileText className="w-5 h-5 text-indigo-200" />
              <div className="text-left">
                <div className="font-medium">Factura Legal</div>
                <div className="text-indigo-200 text-xs">Con timbrado y número de factura fiscal</div>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tarjeta de orden en el Kanban ────────────────────────────────────────────
function OrdenCard({ orden, estadoActual, onAvanzar, onEliminar, onFacturar, onPrint, hasPermission }) {
  const idx = KDS_ESTADOS.indexOf(estadoActual);
  const proximo = KDS_ESTADOS[idx + 1];
  const { badge, icon } = KDS_COLOR[estadoActual] || {};
  const canAvanzar  = canProd(hasPermission, "produccion_kds.avanzar", "produccion.kds_avanzar") || hasPermission(ETAPA_PERM[estadoActual]);
  const canEliminar = canProd(hasPermission, "produccion_kds.eliminar", "produccion.kds_avanzar");
  const canImprimir = canProd(hasPermission, "produccion_kds.imprimir", "produccion.kds_avanzar");
  const canFacturar = canProd(hasPermission, "produccion_facturacion.boleta", "produccion.factura_boleta") || canProd(hasPermission, "produccion_facturacion.factura_legal", "produccion.factura_legal");

  return (
    <div className={`produccion-order-kanban-card produccion-order-kanban-card--${KDS_CLASS[estadoActual] || "a-recibir"} bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm space-y-2 shadow`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-slate-400 text-xs">#{orden.nro_orden}</span>
          <div className="produccion-order-title text-white font-semibold leading-tight">{orden.cliente?.nombre || "—"}</div>
          {orden.cliente?.telefono && (
            <div className="text-slate-400 text-xs">{orden.cliente.telefono}</div>
          )}
        </div>
        <span className={`produccion-status-badge produccion-status-badge--${KDS_CLASS[estadoActual] || "a-recibir"} inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge}`}>
          {icon} {orden.modalidad}
        </span>
      </div>

      {(orden.items || []).map((it, i) => (
          <div key={i} className="produccion-order-item-pill bg-slate-700/50 rounded-lg px-2 py-1.5">
          <div className="produccion-order-item-name text-white text-xs font-medium">{it.cantidad}× {it.nombre_producto}</div>
          {it.variantes_texto && (
            <div className="text-slate-400 text-[11px]">{it.variantes_texto}</div>
          )}
          {it.adicionales_texto && (
            <div className="text-emerald-300 text-[11px]">+ {it.adicionales_texto}</div>
          )}
          {it.observacion && (
            <div className="text-amber-300 text-[11px] italic">📝 {it.observacion}</div>
          )}
        </div>
      ))}

      {orden.modalidad === "DELIVERY" && orden.direccion_entrega && (
        <div className="text-[11px] text-blue-300 bg-blue-950/40 rounded px-2 py-1">
          🚛 {orden.direccion_entrega.zona} — {orden.direccion_entrega.calle}
          {orden.totales?.costo_delivery > 0 && (
            <span className="ml-1 text-slate-400">({fmtGs(orden.totales.costo_delivery)} delivery)</span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-1 border-t border-slate-700">
        <span className="text-emerald-400 font-bold text-xs">{fmtGs(orden.totales?.total_general)}</span>
        <div className="flex gap-1">
          {canImprimir && (
            <button
              onClick={() => onPrint?.(orden)}
              className="p-1 text-slate-400 hover:text-white transition-colors"
              title="Imprimir pedido"
            >
              <Printer className="w-3.5 h-3.5" />
            </button>
          )}
          {canEliminar && (
            <button onClick={() => onEliminar(orden.id)} className="p-1 text-slate-500 hover:text-red-400 transition-colors" title="Eliminar">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {estadoActual === "DESPACHADO" && canFacturar ? (
            <button
              onClick={() => onFacturar(orden)}
              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-white text-xs font-medium flex items-center gap-1"
            >
              <Receipt className="w-3 h-3" /> Facturar
            </button>
          ) : canAvanzar && proximo ? (
            <button
              onClick={() => onAvanzar(orden.id, proximo)}
              className="px-2 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-xs font-medium flex items-center gap-1"
            >
              {proximo} <ChevronRight className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ClienteSearchSelect({ clientes = [], value, onChange, onCreate }) {
  const [open, setOpen] = useState(false);
  const query = (value || "").trim().toLowerCase();
  const filtered = useMemo(() => {
    const base = Array.isArray(clientes) ? clientes : [];
    if (!query) return base.slice(0, 20);
    return base
      .filter(c => `${c.nombre || ""} ${c.razon_social || ""} ${c.ruc || ""} ${c.telefono || ""}`.toLowerCase().includes(query))
      .slice(0, 30);
  }, [clientes, query]);
  const exactMatch = query && clientes.some(c => (c.nombre || c.razon_social || "").trim().toLowerCase() === query);

  return (
      <div className="produccion-client-select relative">
      <input
        value={value}
        onFocus={() => setOpen(true)}
        onChange={e => {
          onChange({ cliente_id: "", cliente_nombre: e.target.value, cliente_ruc: "", cliente_tel: "" });
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        className="produccion-client-input w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
        placeholder="Buscar cliente del catastro..."
      />
      {open && (
        <div className="produccion-client-dropdown absolute z-40 mt-1 w-full max-h-64 overflow-y-auto bg-slate-800 border border-slate-600 rounded-lg shadow-xl">
          {filtered.map(c => {
            const nombre = c.nombre || c.razon_social || "";
            return (
              <button
                key={c.id}
                type="button"
                onMouseDown={() => onChange({
                  cliente_id: c.id || "",
                  cliente_nombre: nombre,
                  cliente_ruc: c.ruc || "",
                  cliente_tel: c.telefono || "",
                })}
                className="produccion-client-option w-full text-left px-3 py-2 hover:bg-slate-700 transition-colors"
              >
                <p className="text-white text-sm font-medium truncate">{nombre}</p>
                <p className="text-slate-400 text-xs truncate">
                  {[c.ruc ? `RUC ${c.ruc}` : "", c.telefono || ""].filter(Boolean).join(" · ") || "Cliente del catastro"}
                </p>
              </button>
            );
          })}
          {query && !exactMatch && (
            <button
              type="button"
              onMouseDown={() => onCreate(value.trim())}
                className="produccion-client-create w-full px-3 py-2 text-left text-emerald-300 hover:bg-emerald-500/10 border-t border-slate-600 text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Crear nuevo cliente "{value.trim()}"
            </button>
          )}
          {filtered.length === 0 && !query && (
            <div className="px-3 py-2 text-slate-400 text-xs">Escribí para buscar un cliente.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab KDS (Kanban + formulario de nueva orden) ─────────────────────────────
function TabKDS({ logo, token, hasPermission, empresas, activeEmpresaPropia, productos, clientesCatastro, cuentasDisp, planCuentas, onClienteCreado }) {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias]   = useState([]);
  const [variantes, setVariantes]     = useState([]);
  const [adicionales, setAdicionales] = useState([]);
  const [zonas, setZonas]             = useState([]);
  const [fichas, setFichas]           = useState([]);
  const [ticketCocina, setTicketCocina] = useState("ticket_80mm");
  const [showForm, setShowForm]       = useState(false);
  const [clienteModalNombre, setClienteModalNombre] = useState("");

  // Form nueva orden
  const defaultItem = () => ({
    producto_id: "",
    producto_sku: "",
    nombre_producto: "",
    categoria_id: "",
    variantes_ids: [],
    adicionales_ids: [],
    observacion: "",
    precio: 0,
    cantidad: 1,
    variante_id: "",
    variante_texto: "",
    ficha_tecnica_id: "",
    ficha_modo_produccion: "",
    iva_tipo: "10",
  });
  const [form, setForm] = useState({ cliente_id: "", cliente_nombre: "", cliente_ruc: "", cliente_tel: "", modalidad: "LOCAL", zona_id: "", calle: "", items: [defaultItem()] });
  const [saving, setSaving] = useState(false);
  const esTemaClaro = String(activeEmpresaPropia?.tema || "").startsWith("claro");

  // Facturación
  const [ordenAFacturar, setOrdenAFacturar] = useState(null);
  const [tipoComprobante, setTipoComprobante] = useState(null); // null | "boleta" | "factura_legal"

  const hdr = { Authorization: `Bearer ${token}` };

  const cargarOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/produccion/ordenes?logo_tipo=${logo}&solo_activas=true`, { headers: hdr });
      if (r.ok) setOrdenes(await r.json());
    } finally { setLoading(false); }
  }, [logo, token]);

  const cargarCatalogos = useCallback(async () => {
    const [rCat, rVar, rAdi, rZon, rFichas] = await Promise.all([
      fetch(`${API}/admin/produccion/catalogos/categorias?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/produccion/catalogos/variantes?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/produccion/catalogos/adicionales?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/produccion/zonas-delivery?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/produccion/fichas-tecnicas?logo_tipo=${logo}`, { headers: hdr }),
    ]);
    if (rCat.ok) setCategorias(await rCat.json());
    if (rVar.ok) setVariantes(await rVar.json());
    if (rAdi.ok) setAdicionales(await rAdi.json());
    if (rZon.ok) setZonas(await rZon.json());
    if (rFichas.ok) setFichas(await rFichas.json());
  }, [logo, token]);

  const cargarConfigImpresion = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/produccion/configuracion?logo_tipo=${logo}`, { headers: hdr });
      if (r.ok) {
        const data = await r.json();
        setTicketCocina(data?.ticket_cocina || "ticket_80mm");
      }
    } catch (_) {
      setTicketCocina("ticket_80mm");
    }
  }, [logo, token]);

  useEffect(() => { cargarOrdenes(); cargarCatalogos(); cargarConfigImpresion(); }, []);

  useSSE({
    logoTipo: logo,
    token,
    enabled: !!logo && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "modulo_actualizado" && event.data?.modulo === "produccion") {
        cargarOrdenes();
        if (event.data?.accion === "config") {
          cargarCatalogos();
          cargarConfigImpresion();
        }
      }
    }, [cargarCatalogos, cargarOrdenes, cargarConfigImpresion]),
  });

  const avanzarEstado = async (id, nuevoEstado) => {
    const r = await fetch(`${API}/admin/produccion/ordenes/${id}/estado`, {
      method: "PATCH",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify({ estado: nuevoEstado }),
    });
    if (r.ok) { toast.success(`Avanzado a ${nuevoEstado}`); cargarOrdenes(); }
    else toast.error("Error al avanzar estado");
  };

  const eliminarOrden = async (id) => {
    if (!window.confirm("¿Eliminar esta orden?")) return;
    await fetch(`${API}/admin/produccion/ordenes/${id}`, { method: "DELETE", headers: hdr });
    cargarOrdenes();
  };

  // Manejo de ítems del formulario
  const setItem = (idx, campo, valor) =>
    setForm(f => { const items = [...f.items]; items[idx] = { ...items[idx], [campo]: valor }; return { ...f, items }; });

  const adicionalesDeItem = useCallback((it) =>
    adicionales.filter(a => (it.adicionales_ids || []).includes(a.id)),
  [adicionales]);

  const variantesTextoDeItem = useCallback((it) => {
    const partes = [];
    if (it.variante_texto) partes.push(it.variante_texto);
    const prodVars = variantes
      .filter(v => (it.variantes_ids || []).includes(v.id))
      .map(v => v.nombre)
      .filter(Boolean);
    if (prodVars.length) partes.push(prodVars.join(" / "));
    return partes.join(" · ");
  }, [variantes]);

  const precioItem = useCallback((it) => {
    const cantidad = Number(it.cantidad || 1);
    const precioBase = Number(it.precio || 0);
    const precioAdicionales = adicionalesDeItem(it).reduce((s, a) => s + Number(a.precio_venta || 0), 0);
    const precioUnitario = precioBase + precioAdicionales;
    return {
      cantidad,
      precioBase,
      precioAdicionales,
      precioUnitario,
      subtotal: precioUnitario * cantidad,
    };
  }, [adicionalesDeItem]);

  const descripcionItem = useCallback((it) => {
    const partes = [it.nombre_producto || "Ítem"];
    const varTxt = it.variantes_texto || variantesTextoDeItem(it);
    const adiTxt = it.adicionales_texto || adicionalesDeItem(it).map(a => a.nombre).filter(Boolean).join(" / ");
    if (varTxt) partes.push(varTxt);
    if (adiTxt) partes.push(`Adicionales: ${adiTxt}`);
    return partes.join(" - ");
  }, [adicionalesDeItem, variantesTextoDeItem]);

  const seleccionarProducto = (idx, productoId, producto, variante = null) => {
    setForm(f => {
      const items = [...f.items];
      const actual = items[idx] || defaultItem();
      if (!producto) {
        items[idx] = { ...actual, producto_id: "", producto_sku: "", variante_id: "", variante_texto: "", ficha_tecnica_id: "", ficha_modo_produccion: "" };
        return { ...f, items };
      }
      const categoriaMatch = categorias.find(c =>
        c.id === producto.categoria ||
        (c.nombre || "").trim().toLowerCase() === (producto.categoria || "").trim().toLowerCase()
      );
      const precio = Number(variante?.precio_venta ?? producto.precio_venta ?? 0);
      const ficha = fichas.find(x => x.producto_id === (productoId || producto.id));
      items[idx] = {
        ...actual,
        producto_id: productoId || producto.id || "",
        producto_sku: producto.sku || "",
        nombre_producto: producto.nombre || actual.nombre_producto,
        precio,
        categoria_id: categoriaMatch?.id || actual.categoria_id,
        variante_id: variante?.id || "",
        variante_texto: variante?.atributos_texto || "",
        ficha_tecnica_id: ficha?.id || "",
        ficha_modo_produccion: ficha?.modo_produccion || "",
        iva_tipo: producto.iva_tipo || actual.iva_tipo || "10",
      };
      return { ...f, items };
    });
  };

  const calcTotal = () => {
    const subtotal = form.items.reduce((s, it) => s + precioItem(it).subtotal, 0);
    const zona = zonas.find(z => z.id === form.zona_id);
    const delivery = form.modalidad === "DELIVERY" ? (zona?.costo_delivery || 0) : 0;
    return { subtotal, delivery, total: subtotal + delivery };
  };

  const facturaInicialOrden = useMemo(() => {
    if (!ordenAFacturar) return null;
    const conceptos = (ordenAFacturar.items || []).map(it => ({
      descripcion: descripcionItem(it),
      cantidad: Number(it.cantidad || 1),
      precio_unitario: Number(it.precio_unitario_final ?? it.precio ?? 0),
      precio_costo: Number(it.precio_costo || 0),
      subtotal: Number(it.subtotal ?? (Number(it.cantidad || 1) * Number(it.precio_unitario_final ?? it.precio ?? 0))),
      producto_id: it.producto_id || "",
      producto_sku: it.producto_sku || "",
      variante_id: it.variante_id || "",
      variante_texto: it.variante_texto || "",
      ficha_tecnica_id: it.ficha_tecnica_id || "",
      ficha_modo_produccion: it.ficha_modo_produccion || "",
      iva_tipo: it.iva_tipo || "10",
    }));
    const conceptosFactura = conceptos.map(c =>
      c.ficha_tecnica_id && c.ficha_modo_produccion !== "stock_terminado"
        ? { ...c, producto_id: "", variante_id: "" }
        : c
    );
    const delivery = Number(ordenAFacturar.totales?.costo_delivery || 0);
    if (delivery > 0) {
      conceptos.push({
        descripcion: `Delivery${ordenAFacturar.direccion_entrega?.zona ? ` - ${ordenAFacturar.direccion_entrega.zona}` : ""}`,
        cantidad: 1,
        precio_unitario: delivery,
        precio_costo: 0,
        subtotal: delivery,
        producto_id: "",
        producto_sku: "",
        variante_id: "",
        variante_texto: "",
        iva_tipo: "10",
      });
    }
    return {
      __source_id: ordenAFacturar.id,
      _empresa_id: ordenAFacturar.cliente?.id || "",
      empresa_id: ordenAFacturar.cliente?.id || "",
      empresa_nombre: ordenAFacturar.cliente?.nombre || "",
      razon_social: ordenAFacturar.cliente?.nombre || "",
      ruc: ordenAFacturar.cliente?.ruc || "",
      forma_pago: "contado",
      moneda: "PYG",
      modo: conceptosFactura.some(c => c.producto_id) ? "productos" : "libre",
      afecta_stock: conceptosFactura.some(c => c.producto_id),
      conceptos: conceptosFactura,
      notas: `Orden de producción #${ordenAFacturar.nro_orden || ordenAFacturar.id}`,
      _razon_social_locked: !!ordenAFacturar.cliente?.id,
    };
  }, [ordenAFacturar, descripcionItem]);

  const guardarOrden = async () => {
    if (!form.cliente_id) { toast.error("Seleccioná un cliente del catastro o creá uno nuevo"); return; }
    if (form.items.length === 0 || form.items.every(it => !it.nombre_producto.trim() && !it.precio)) {
      toast.error("Agregue al menos un ítem"); return;
    }
    setSaving(true);
    try {
      const zona = zonas.find(z => z.id === form.zona_id);
      const totales = calcTotal();
      const payload = {
        cliente: {
          id: form.cliente_id || null,
          nombre: form.cliente_nombre.trim(),
          ruc: form.cliente_ruc.trim() || null,
          telefono: form.cliente_tel.trim(),
          logo_tipo: logo,
        },
        modalidad: form.modalidad,
        direccion_entrega: form.modalidad === "DELIVERY"
          ? { zona: zona?.nombre || form.zona_id, calle: form.calle, costo_delivery: totales.delivery }
          : null,
        items: form.items.filter(it => it.nombre_producto.trim() || it.precio > 0).map(it => {
          const pricing = precioItem(it);
          const adicionalesTexto = adicionalesDeItem(it).map(a => a.nombre).filter(Boolean).join(" / ");
          const variantesTexto = variantesTextoDeItem(it);
          return {
            producto_id: it.producto_id || null,
            producto_sku: it.producto_sku || null,
            nombre_producto: it.nombre_producto.trim(),
            categoria_id: it.categoria_id,
            variantes_ids: it.variantes_ids,
            adicionales_ids: it.adicionales_ids,
            variantes_texto: variantesTexto,
            adicionales_texto: adicionalesTexto,
            observacion: it.observacion.trim(),
            precio: pricing.precioBase,
            precio_base: pricing.precioBase,
            precio_adicionales: pricing.precioAdicionales,
            precio_unitario_final: pricing.precioUnitario,
            cantidad: pricing.cantidad,
            subtotal: pricing.subtotal,
            variante_id: it.variante_id || null,
            variante_texto: it.variante_texto || "",
            ficha_tecnica_id: it.ficha_tecnica_id || null,
            ficha_modo_produccion: it.ficha_modo_produccion || "",
            iva_tipo: it.iva_tipo || "10",
            requiere_bom: it.variantes_ids.length > 0 || !!it.categoria_id,
            config_bom: { codigo_categoria: it.categoria_id, variantes_ids: it.variantes_ids },
          };
        }),
        totales: { subtotal: totales.subtotal, costo_delivery: totales.delivery, total_general: totales.total },
      };
      const r = await fetch(`${API}/admin/produccion/ordenes?logo_tipo=${logo}`, {
        method: "POST",
        headers: { ...hdr, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success("Orden enviada a cocina");
        setForm({ cliente_id: "", cliente_nombre: "", cliente_ruc: "", cliente_tel: "", modalidad: "LOCAL", zona_id: "", calle: "", items: [defaultItem()] });
        setShowForm(false);
        cargarOrdenes();
      } else {
        const err = await r.json();
        toast.error(err.detail || "Error al crear orden");
      }
    } finally { setSaving(false); }
  };

  // Facturación: cuando se elige el tipo de comprobante
  const handleTipoElegido = (tipo) => {
    setTipoComprobante(tipo);
    setOrdenAFacturar(prev => prev); // mantiene la orden
  };

  const handleFacturaSaved = async (savedData) => {
    if (!ordenAFacturar) return;
    // Marcar la orden como facturada en el backend
    await fetch(`${API}/admin/produccion/ordenes/${ordenAFacturar.id}/facturar`, {
      method: "PATCH",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify({ factura_id: savedData?.id, tipo_comprobante: tipoComprobante }),
    });
    toast.success("Orden facturada correctamente");
    setOrdenAFacturar(null);
    setTipoComprobante(null);
    cargarOrdenes();
  };

  const imprimirPedido = useCallback((orden) => {
    if (!orden) return;
    const modoTicket = (ticketCocina || "ticket_80mm") !== "a4_operativo";
    const itemsHtml = (orden.items || []).map(it => {
      const varTxt = it.variantes_texto || "";
      const adiTxt = it.adicionales_texto || "";
      const obs = it.observacion || "";
      const precio = Number(it.precio_unitario_final ?? it.precio ?? 0);
      return modoTicket ? `
        <div class="item">
          <div class="item-top">
            <span class="qty">${it.cantidad || 1}x</span>
            <span class="desc">${it.nombre_producto || ""}</span>
            <span class="price">${fmtGs(precio)}</span>
          </div>
          ${varTxt ? `<div class="muted">${varTxt}</div>` : ""}
          ${adiTxt ? `<div class="ok">+ ${adiTxt}</div>` : ""}
          ${obs ? `<div class="note">${obs}</div>` : ""}
        </div>
      ` : `
        <tr>
          <td>${it.cantidad || 1}</td>
          <td>
            <strong>${it.nombre_producto || ""}</strong>
            ${varTxt ? `<div class="muted">${varTxt}</div>` : ""}
            ${adiTxt ? `<div class="ok">Adicionales: ${adiTxt}</div>` : ""}
            ${obs ? `<div class="note">${obs}</div>` : ""}
          </td>
          <td class="right">${fmtGs(precio)}</td>
        </tr>
      `;
    }).join("");
    const html = modoTicket ? `
      <!doctype html>
      <html>
        <head>
          <title>Ticket pedido #${orden.nro_orden || ""}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @media print {
              @page { size: 80mm auto; margin: 0; }
              body { width: 80mm; padding: 4px 4px 16px; }
              .no-print { display: none !important; }
            }
            body {
              width: 80mm;
              margin: 0 auto;
              padding: 10px 6px 20px;
              background: #fff;
              color: #000;
              font-family: "Courier New", Courier, monospace;
              font-size: 11px;
            }
            .center { text-align: center; }
            .right { text-align: right; }
            .bold { font-weight: 700; }
            .title { font-size: 16px; font-weight: 900; text-align: center; line-height: 1.15; }
            .sub { font-size: 9px; color: #444; line-height: 1.45; }
            .sep { text-align: center; color: #444; margin: 5px 0; white-space: nowrap; overflow: hidden; }
            .box { border: 1px dashed #999; padding: 5px 6px; margin: 5px 0; }
            .row { display: flex; justify-content: space-between; gap: 6px; line-height: 1.55; }
            .label { color: #555; }
            .item { padding: 4px 0; border-bottom: 1px dashed #ddd; }
            .item-top { display: flex; gap: 4px; align-items: flex-start; }
            .qty { width: 28px; flex-shrink: 0; text-align: center; font-weight: 700; }
            .desc { flex: 1; font-weight: 700; word-break: break-word; }
            .price { width: 70px; flex-shrink: 0; text-align: right; }
            .muted { color: #555; font-size: 9px; padding-left: 30px; margin-top: 1px; }
            .ok { color: #047857; font-size: 9px; padding-left: 30px; margin-top: 1px; }
            .note { color: #92400e; font-size: 9px; padding-left: 30px; margin-top: 1px; font-style: italic; }
            .total { font-size: 14px; font-weight: 900; text-align: right; margin-top: 6px; }
            .btn-print { width: 100%; margin-top: 14px; padding: 9px; border: 0; border-radius: 6px; background: #1d4ed8; color: #fff; font-family: Arial, sans-serif; font-weight: 700; cursor: pointer; }
          </style>
        </head>
        <body>
          <div class="title">PEDIDO #${orden.nro_orden || ""}</div>
          <div class="center sub">${new Date(orden.fecha_creacion || Date.now()).toLocaleString("es-PY")}</div>
          <div class="sep">================================</div>
          <div class="box">
            <div class="bold">${orden.cliente?.nombre || "Cliente"}</div>
            ${orden.cliente?.telefono ? `<div class="sub">${orden.cliente.telefono}</div>` : ""}
            <div class="row"><span class="label">Modalidad</span><span class="bold">${orden.modalidad || ""}</span></div>
            ${orden.direccion_entrega ? `<div class="sub">${orden.direccion_entrega.zona || ""} ${orden.direccion_entrega.calle || ""}</div>` : ""}
          </div>
          <div class="sep">--------------------------------</div>
          ${itemsHtml}
          <div class="sep">--------------------------------</div>
          ${Number(orden.totales?.costo_delivery || 0) > 0 ? `<div class="row sub"><span>Delivery</span><span>${fmtGs(orden.totales.costo_delivery)}</span></div>` : ""}
          <div class="total">TOTAL: ${fmtGs(orden.totales?.total_general)}</div>
          <div class="sep">================================</div>
          <button class="btn-print no-print" onclick="window.print()">Imprimir Ticket</button>
          <script>setTimeout(() => window.print(), 300);</script>
        </body>
      </html>
    ` : `
      <!doctype html>
      <html>
        <head>
          <title>Pedido #${orden.nro_orden || ""}</title>
          <style>
            @page { margin: 10mm; }
            body { font-family: Arial, sans-serif; color: #111827; margin: 0; }
            .wrap { width: 80mm; margin: 0 auto; }
            h1 { font-size: 18px; margin: 0 0 6px; }
            .muted { color: #6b7280; font-size: 11px; margin-top: 2px; }
            .ok { color: #047857; font-size: 11px; margin-top: 2px; }
            .note { color: #b45309; font-size: 11px; margin-top: 2px; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 8px; margin: 8px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            td { border-top: 1px solid #e5e7eb; padding: 6px 2px; vertical-align: top; }
            .right { text-align: right; white-space: nowrap; }
            .total { font-size: 15px; font-weight: 700; text-align: right; margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="wrap">
            <h1>Pedido #${orden.nro_orden || ""}</h1>
            <div class="muted">${new Date(orden.fecha_creacion || Date.now()).toLocaleString("es-PY")}</div>
            <div class="box">
              <strong>${orden.cliente?.nombre || "Cliente"}</strong>
              ${orden.cliente?.telefono ? `<div class="muted">${orden.cliente.telefono}</div>` : ""}
              <div class="muted">${orden.modalidad || ""}</div>
              ${orden.direccion_entrega ? `<div class="muted">${orden.direccion_entrega.zona || ""} ${orden.direccion_entrega.calle || ""}</div>` : ""}
            </div>
            <table><tbody>${itemsHtml}</tbody></table>
            ${Number(orden.totales?.costo_delivery || 0) > 0 ? `<div class="right muted">Delivery: ${fmtGs(orden.totales.costo_delivery)}</div>` : ""}
            <div class="total">Total: ${fmtGs(orden.totales?.total_general)}</div>
          </div>
          <script>window.print();</script>
        </body>
      </html>
    `;
    const win = window.open("", "_blank", modoTicket ? "width=360,height=720" : "width=420,height=720");
    if (!win) {
      toast.error("El navegador bloqueó la ventana de impresión");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  }, [ticketCocina]);

  const ordenesTotales = calcTotal();

  return (
    <div className="space-y-4">
      {/* Botonera superior */}
      <div className="produccion-toolbar flex items-center justify-between">
        <h2 className="produccion-section-title text-white font-semibold text-lg flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-indigo-400" /> Panel de Cocina (KDS)
        </h2>
        <div className="flex gap-2">
          <button onClick={cargarOrdenes} className="produccion-refresh-btn p-2 text-slate-400 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors" title="Actualizar">
            <RefreshCw className="w-4 h-4" />
          </button>
          {canProd(hasPermission, "produccion_kds.tomar_orden", "produccion.tomar_orden") && (
            <button
              onClick={() => setShowForm(v => !v)}
              className="produccion-primary-btn flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Nueva orden
            </button>
          )}
        </div>
      </div>

      {/* Formulario nueva orden */}
      {showForm && (
        <div className="produccion-order-card border rounded-xl p-4 space-y-4">
          <h3 className="produccion-heading font-medium flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-indigo-400" /> Nueva Orden
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="produccion-label text-xs block mb-1">Cliente *</label>
              <ClienteSearchSelect
                clientes={clientesCatastro}
                value={form.cliente_nombre}
                onChange={data => setForm(f => ({ ...f, ...data }))}
                onCreate={nombre => setClienteModalNombre(nombre)}
              />
              {form.cliente_id && (
                <p className="text-emerald-400 text-[11px] mt-1">
                  Cliente vinculado{form.cliente_ruc ? ` · RUC ${form.cliente_ruc}` : ""}
                </p>
              )}
            </div>
            <div>
              <label className="produccion-label text-xs block mb-1">Teléfono</label>
              <input
                value={form.cliente_tel}
                onChange={e => setForm(f => ({ ...f, cliente_tel: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="0981 000 000"
              />
            </div>
            <div>
              <label className="produccion-label text-xs block mb-1">Modalidad</label>
              <select
                value={form.modalidad}
                onChange={e => setForm(f => ({ ...f, modalidad: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="LOCAL">Retiro local</option>
                <option value="DELIVERY">Delivery</option>
              </select>
            </div>
          </div>

          {form.modalidad === "DELIVERY" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="produccion-label text-xs block mb-1">Zona de entrega</label>
                <select
                  value={form.zona_id}
                  onChange={e => setForm(f => ({ ...f, zona_id: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">— Seleccionar zona —</option>
                  {zonas.map(z => (
                    <option key={z.id} value={z.id}>{z.nombre} ({fmtGs(z.costo_delivery)})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="produccion-label text-xs block mb-1">Dirección</label>
                <input
                  value={form.calle}
                  onChange={e => setForm(f => ({ ...f, calle: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="Calle y número"
                />
              </div>
            </div>
          )}

          {/* Ítems */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="produccion-subtitle text-slate-300 text-sm font-medium">Ítems del pedido</span>
              <button
                onClick={() => setForm(f => ({ ...f, items: [...f.items, defaultItem()] }))}
                className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Agregar ítem
              </button>
            </div>

            {form.items.map((it, idx) => (
              <div key={idx} className="produccion-item-card rounded-lg p-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="sm:col-span-2">
                    <label className="produccion-label text-xs block mb-1">Producto del catálogo *</label>
                    {productos.length > 0 ? (
                      <div className="space-y-1.5">
                        <ProductoSearchSelect
                          productos={productos}
                          value={it.producto_id || ""}
                        onChange={(productoId, producto, variante) => seleccionarProducto(idx, productoId, producto, variante)}
                        placeholder="Buscar producto del catálogo..."
                        theme={esTemaClaro ? "light" : "dark"}
                        includeVariants={true}
                        variantValue={it.variante_id || ""}
                      />
                        <input
                          value={it.nombre_producto}
                          onChange={e => {
                            setItem(idx, "nombre_producto", e.target.value);
                            if (it.producto_id) {
                              setItem(idx, "producto_id", "");
                              setItem(idx, "producto_sku", "");
                              setItem(idx, "variante_id", "");
                              setItem(idx, "variante_texto", "");
                              setItem(idx, "ficha_tecnica_id", "");
                              setItem(idx, "ficha_modo_produccion", "");
                            }
                          }}
                          className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                          placeholder="Nombre libre o ajuste del producto"
                        />
                      </div>
                    ) : (
                      <input
                        value={it.nombre_producto}
                        onChange={e => setItem(idx, "nombre_producto", e.target.value)}
                        className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                        placeholder="Nombre del producto"
                      />
                    )}
                  </div>
                  <div>
                    <label className="produccion-label text-xs block mb-1">Precio (Gs.)</label>
                    <input
                      type="number" min={0}
                      value={it.precio}
                      onChange={e => setItem(idx, "precio", e.target.value)}
                      className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="produccion-label text-xs block mb-1">Cantidad</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min={1}
                        value={it.cantidad}
                        onChange={e => setItem(idx, "cantidad", e.target.value)}
                        className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                      />
                      {form.items.length > 1 && (
                        <button onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-300 shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Categoría y variantes (opcionales) */}
                {(categorias.length > 0 || variantes.length > 0) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {categorias.length > 0 && (
                    <div>
                      <label className="produccion-label text-xs block mb-1">Categoría</label>
                      <select
                        value={it.categoria_id}
                        onChange={e => setItem(idx, "categoria_id", e.target.value)}
                        className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                      >
                        <option value="">— Sin categoría —</option>
                        {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                    )}
                    {variantes.length > 0 && (
                      <div>
                        <label className="produccion-label text-xs block mb-1">Variantes (selección múltiple)</label>
                        <div className="produccion-option-grid">
                          {variantes.map(v => (
                            <label
                              key={v.id}
                              className={`produccion-option-chip ${it.variantes_ids.includes(v.id) ? "produccion-option-chip--checked" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={it.variantes_ids.includes(v.id)}
                                onChange={e => {
                                  const arr = e.target.checked
                                    ? [...it.variantes_ids, v.id]
                                    : it.variantes_ids.filter(x => x !== v.id);
                                  setItem(idx, "variantes_ids", arr);
                                }}
                                className="produccion-option-checkbox accent-indigo-500"
                              />
                              <span>{v.nombre}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Adicionales */}
                {adicionales.length > 0 && (
                  <div>
                    <label className="produccion-label text-xs block mb-1">Adicionales</label>
                    <div className="produccion-option-grid">
                      {adicionales.map(a => (
                        <label
                          key={a.id}
                          className={`produccion-option-chip ${it.adicionales_ids.includes(a.id) ? "produccion-option-chip--checked" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={it.adicionales_ids.includes(a.id)}
                            onChange={e => {
                              const arr = e.target.checked
                                ? [...it.adicionales_ids, a.id]
                                : it.adicionales_ids.filter(x => x !== a.id);
                              setItem(idx, "adicionales_ids", arr);
                            }}
                            className="produccion-option-checkbox accent-indigo-500"
                          />
                          <span>{a.nombre}</span>
                          {a.precio_venta > 0 && <span className="produccion-option-price">+{fmtGs(a.precio_venta)}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="produccion-item-summary text-right text-[11px] text-slate-300">
                  {it.ficha_tecnica_id && (
                    <span className="mr-2 text-emerald-300">
                      Ficha técnica · {it.ficha_modo_produccion === "stock_terminado" ? "stock terminado" : "bajo demanda"}
                    </span>
                  )}
                  Unitario: <span className="text-white font-semibold">{fmtGs(precioItem(it).precioUnitario)}</span>
                  <span className="mx-1 text-slate-500">·</span>
                  Subtotal: <span className="text-emerald-300 font-semibold">{fmtGs(precioItem(it).subtotal)}</span>
                </div>

                <div>
                  <label className="produccion-label text-xs block mb-1">Nota de cocina</label>
                  <input
                    value={it.observacion}
                    onChange={e => setItem(idx, "observacion", e.target.value)}
                    className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-xs"
                    placeholder="Instrucciones especiales..."
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Totales + botón guardar */}
          <div className="produccion-form-footer flex items-center justify-between pt-2 border-t border-slate-700">
            <div className="text-sm text-slate-300 space-x-4">
              <span>Subtotal: <span className="text-white font-medium">{fmtGs(ordenesTotales.subtotal)}</span></span>
              {form.modalidad === "DELIVERY" && (
                <span>Delivery: <span className="text-amber-400 font-medium">{fmtGs(ordenesTotales.delivery)}</span></span>
              )}
              <span className="text-emerald-400 font-bold">Total: {fmtGs(ordenesTotales.total)}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="produccion-ghost-btn px-3 py-2 text-slate-400 hover:text-white text-sm">Cancelar</button>
              <button
                onClick={guardarOrden}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-lg text-white text-sm font-medium"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar a cocina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kanban KDS */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando órdenes...
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {etapasVisibles(hasPermission).map(estado => {
            const items = ordenes.filter(o => o.estado_kds === estado);
            const { bg, badge } = KDS_COLOR[estado];
            return (
              <div key={estado} className={`produccion-kds-column produccion-kds-column--${KDS_CLASS[estado]} rounded-xl p-3 min-h-[200px] ${bg} border border-white/5`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`produccion-status-badge produccion-status-badge--${KDS_CLASS[estado]} inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${badge}`}>
                    {KDS_COLOR[estado].icon} {estado}
                  </span>
                  <span className="text-slate-400 text-xs">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.length === 0 && (
                    <p className="produccion-empty text-slate-400 text-xs text-center py-4">Sin pedidos</p>
                  )}
                  {items.map(o => (
                    <OrdenCard
                      key={o.id}
                      orden={o}
                      estadoActual={estado}
                      onAvanzar={avanzarEstado}
                      onEliminar={eliminarOrden}
                      onFacturar={setOrdenAFacturar}
                      onPrint={imprimirPedido}
                      hasPermission={hasPermission}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal selector boleta/factura legal */}
      {ordenAFacturar && !tipoComprobante && (
        <TipoComprobantePicker
          orden={ordenAFacturar}
          hasPermission={hasPermission}
          onSelect={handleTipoElegido}
          onClose={() => setOrdenAFacturar(null)}
        />
      )}

      {/* FacturaFormModal (boleta o factura legal) */}
      {ordenAFacturar && tipoComprobante && (
        <FacturaFormModal
          factura={null}
          sinFactura={tipoComprobante === "boleta"}
          onClose={() => { setOrdenAFacturar(null); setTipoComprobante(null); }}
          onSaved={handleFacturaSaved}
          token={token}
          API={API}
          empresas={empresas}
          presupuestosDisp={[]}
          activeEmpresaPropia={activeEmpresaPropia}
          hasPermission={hasPermission}
          productos={productos}
          productosHabilitados={productos.length > 0}
          cuentasDisp={cuentasDisp}
          planCuentasDisp={planCuentas}
          initialValues={facturaInicialOrden}
          defaultEmpresaId={ordenAFacturar?.cliente?.id || ""}
        />
      )}

      {clienteModalNombre !== "" && (
        <EntidadModal
          entidad={{
            _presetRol: "es_cliente",
            _initialNombre: clienteModalNombre,
            _initialRazonSocial: clienteModalNombre,
          }}
          logo={logo}
          token={token}
          onClose={() => setClienteModalNombre("")}
          allowedRoles={{ es_cliente: true, es_proveedor: false, es_empleado: false }}
          onSaved={cliente => {
            if (!cliente?.id) {
              setClienteModalNombre("");
              return;
            }
            onClienteCreado?.(cliente);
            setForm(f => ({
              ...f,
              cliente_id: cliente.id || "",
              cliente_nombre: cliente.nombre || "",
              cliente_ruc: cliente.ruc || "",
              cliente_tel: cliente.telefono || "",
            }));
            setClienteModalNombre("");
          }}
        />
      )}
    </div>
  );
}

// ── Sección de catálogo genérica (Categorías / Variantes / Adicionales) ──────
function SeccionCatalogo({ titulo, icono, logo, token, coleccion, conPrecio = false }) {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]     = useState({ id: "", nombre: "", precio_venta: 0 });
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const hdr = { Authorization: `Bearer ${token}` };
  const endpoint = `${API}/admin/produccion/catalogos/${coleccion}`;

  const cargar = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${endpoint}?logo_tipo=${logo}`, { headers: hdr });
    if (r.ok) setItems(await r.json());
    setLoading(false);
  }, [logo, token]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("Ingrese un nombre"); return; }
    setSaving(true);
    const id = form.id || slugId(form.nombre) + "_" + Date.now();
    const r = await fetch(`${endpoint}/${id}?logo_tipo=${logo}`, {
      method: "PUT",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify({ nombre: form.nombre.trim(), precio_venta: Number(form.precio_venta) }),
    });
    setSaving(false);
    if (r.ok) { toast.success("Guardado"); setForm({ id: "", nombre: "", precio_venta: 0 }); setEditando(false); cargar(); }
    else toast.error("Error al guardar");
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await fetch(`${endpoint}/${id}?logo_tipo=${logo}`, { method: "DELETE", headers: hdr });
    cargar();
  };

  return (
    <div className="produccion-config-card bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="produccion-config-title text-white font-medium flex items-center gap-2">{icono} {titulo}</h3>
        <button onClick={() => { setForm({ id: "", nombre: "", precio_venta: 0 }); setEditando(true); }}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Agregar
        </button>
      </div>

      {editando && (
        <div className="produccion-config-form bg-slate-700/50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm" />
            </div>
            {conPrecio && (
              <div>
                <label className="text-slate-400 text-xs block mb-1">Precio venta (Gs.)</label>
                <input type="number" min={0} value={form.precio_venta}
                  onChange={e => setForm(f => ({ ...f, precio_venta: e.target.value }))}
                  className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm" />
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditando(false)} className="text-slate-400 text-xs hover:text-white">Cancelar</button>
            <button onClick={guardar} disabled={saving}
              className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-xs flex items-center gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-500 text-sm text-center py-3">Cargando...</div> : items.length === 0
        ? <div className="text-slate-400 text-sm text-center py-3">Sin registros</div>
        : (
          <div className="produccion-config-list divide-y divide-slate-700">
            {items.map(it => (
              <div key={it.id} className="produccion-config-row flex items-center justify-between py-2">
                <div>
                  <span className="text-white text-sm">{it.nombre}</span>
                  {conPrecio && it.precio_venta > 0 && (
                    <span className="text-emerald-400 text-xs ml-2">{fmtGs(it.precio_venta)}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setForm({ id: it.id, nombre: it.nombre, precio_venta: it.precio_venta || 0 }); setEditando(true); }}
                    className="p-1 text-slate-400 hover:text-indigo-400"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => eliminar(it.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── Tab Configuración ─────────────────────────────────────────────────────────
function ConfigModoProduccion({ logo, token }) {
  const [config, setConfig] = useState({ modo_default: "bajo_demanda", desperdicio_default_pct: 5, ticket_cocina: "ticket_80mm" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const hdr = { Authorization: `Bearer ${token}` };

  const cargar = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/admin/produccion/configuracion?logo_tipo=${logo}`, { headers: hdr });
    if (r.ok) setConfig(await r.json());
    setLoading(false);
  }, [logo, token]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setSaving(true);
    const r = await fetch(`${API}/admin/produccion/configuracion?logo_tipo=${logo}`, {
      method: "PUT",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
    setSaving(false);
    if (r.ok) {
      setConfig(await r.json());
      toast.success("Configuración guardada");
    } else {
      toast.error("No se pudo guardar la configuración");
    }
  };

  if (loading) return <div className="text-slate-500 text-sm py-6">Cargando configuración...</div>;

  return (
    <div className="produccion-config-card bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-4">
      <div>
        <h3 className="produccion-config-title text-white font-medium flex items-center gap-2"><Factory className="w-4 h-4 text-indigo-400" /> Modo de producción</h3>
        <p className="text-slate-400 text-xs mt-1">Define el modo sugerido para nuevas fichas técnicas. Cada ficha puede tener su propio modo.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          ["bajo_demanda", "Bajo demanda", "Al facturar el pedido, se descuentan los insumos de la ficha técnica."],
          ["stock_terminado", "Stock terminado", "Primero se produce y entra stock del producto terminado; la venta descuenta ese producto."],
        ].map(([id, label, desc]) => (
          <button
            key={id}
            type="button"
            onClick={() => setConfig(c => ({ ...c, modo_default: id }))}
            className={`produccion-mode-card text-left rounded-xl border p-4 transition-colors ${config.modo_default === id ? "produccion-mode-card--active border-emerald-400 bg-emerald-500/10" : "border-slate-600 bg-slate-900/30 hover:bg-slate-700/40"}`}
          >
            <div className="text-white font-semibold text-sm">{label}</div>
            <div className="text-slate-400 text-xs mt-1">{desc}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-slate-400 text-xs block mb-1">Desperdicio sugerido (%)</label>
          <input
            type="number"
            min={0}
            value={config.desperdicio_default_pct || 0}
            onChange={e => setConfig(c => ({ ...c, desperdicio_default_pct: e.target.value }))}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="text-slate-400 text-xs block mb-1">Modelo de impresión cocina</label>
          <select
            value={config.ticket_cocina || "ticket_80mm"}
            onChange={e => setConfig(c => ({ ...c, ticket_cocina: e.target.value }))}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="ticket_80mm">Ticket cocina 80mm</option>
            <option value="a4_operativo">A4 operativo</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={guardar} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar configuración
        </button>
      </div>
    </div>
  );
}

function FichasTecnicas({ logo, token, productos }) {
  const [fichas, setFichas] = useState([]);
  const [config, setConfig] = useState({ modo_default: "bajo_demanda", desperdicio_default_pct: 5 });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const hdr = { Authorization: `Bearer ${token}` };

  const cargar = useCallback(async () => {
    const [rFichas, rCfg] = await Promise.all([
      fetch(`${API}/admin/produccion/fichas-tecnicas?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/produccion/configuracion?logo_tipo=${logo}`, { headers: hdr }),
    ]);
    if (rFichas.ok) setFichas(await rFichas.json());
    if (rCfg.ok) setConfig(await rCfg.json());
  }, [logo, token]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const nuevaFicha = () => setForm({
    id: "",
    producto_id: "",
    nombre: "",
    categoria: "",
    tamano: "",
    tiempo_preparacion_min: 0,
    rendimiento: 1,
    modo_produccion: config.modo_default || "bajo_demanda",
    desperdicio_pct: config.desperdicio_default_pct || 0,
    precio_venta_sugerido: 0,
    instrucciones: "",
    ingredientes: [],
  });

  const editar = (f) => setForm({
    ...f,
    rendimiento: f.rendimiento || 1,
    // fichas viejas: precio por envase / cantidad de envase -> precio unitario
    ingredientes: (f.ingredientes || []).map(i => ({
      ...i,
      precio_unitario: i.precio_unitario != null ? i.precio_unitario
        : (Number(i.cantidad_envase || 1) ? Number(i.precio_envase || 0) / Number(i.cantidad_envase || 1) : 0),
    })),
  });

  const productoSeleccionado = productos.find(p => p.id === form?.producto_id);
  const ingredientesCalc = useMemo(() => (form?.ingredientes || []).map(i => {
    const precioUnit = Number(i.precio_unitario || 0);
    const cantidadUsada = Number(i.cantidad_usada || 0);
    const factor = Number(i.factor_correccion || 1);
    const costo = precioUnit * cantidadUsada * factor;
    return { ...i, costo_proporcional: costo };
  }), [form]);
  const costoInsumos = ingredientesCalc.reduce((s, i) => s + Number(i.costo_proporcional || 0), 0);
  const desperdicio = costoInsumos * Number(form?.desperdicio_pct || 0) / 100;
  const costoLote = costoInsumos + desperdicio;
  const rendimiento = Number(form?.rendimiento) > 0 ? Number(form.rendimiento) : 1;
  const costoFinal = costoLote / rendimiento;   // costo de UNA unidad terminada
  const margen = Number(form?.precio_venta_sugerido || 0) ? ((Number(form.precio_venta_sugerido || 0) - costoFinal) / Number(form.precio_venta_sugerido || 0)) * 100 : 0;

  const setIng = (idx, campo, valor) => setForm(f => {
    const ingredientes = [...(f.ingredientes || [])];
    ingredientes[idx] = { ...ingredientes[idx], [campo]: valor };
    return { ...f, ingredientes };
  });

  const seleccionarIngrediente = (idx, productoId) => {
    const p = productos.find(x => x.id === productoId);
    setForm(f => {
      const ingredientes = [...(f.ingredientes || [])];
      ingredientes[idx] = {
        ...ingredientes[idx],
        producto_id: p?.id || "",
        producto_nombre: p?.nombre || "",
        sku: p?.sku || "",
        unidad_medida: p?.unidad || "unidad",
        precio_unitario: Number(p?.precio_costo || 0),
      };
      return { ...f, ingredientes };
    });
  };

  const guardar = async () => {
    if (!form.producto_id) { toast.error("Seleccioná el producto terminado"); return; }
    setSaving(true);
    const id = form.id || `FICHA_${Date.now()}`;
    const r = await fetch(`${API}/admin/produccion/fichas-tecnicas/${id}?logo_tipo=${logo}`, {
      method: "PUT",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) {
      toast.success("Ficha técnica guardada");
      setForm(null);
      cargar();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || "No se pudo guardar la ficha");
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta ficha técnica?")) return;
    await fetch(`${API}/admin/produccion/fichas-tecnicas/${id}?logo_tipo=${logo}`, { method: "DELETE", headers: hdr });
    cargar();
  };

  const producir = async (ficha) => {
    const cantidad = Number(window.prompt(`Cantidad a producir de ${ficha.nombre} (la receta rinde ${ficha.rendimiento || 1})`, String(ficha.rendimiento || 1)) || 0);
    if (!cantidad) return;
    const r = await fetch(`${API}/admin/produccion/fichas-tecnicas/${ficha.id}/producir?logo_tipo=${logo}`, {
      method: "POST",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify({ cantidad }),
    });
    if (r.ok) toast.success("Stock producido correctamente");
    else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || "No se pudo producir stock");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium flex items-center gap-2"><Factory className="w-4 h-4 text-emerald-400" /> Fichas técnicas</h3>
          <p className="text-slate-400 text-xs mt-1">Recetas por producto terminado, con consumo de insumos y costo gerencial.</p>
        </div>
        <button onClick={nuevaFicha} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nueva ficha
        </button>
      </div>

      {form && (
        <div className="produccion-config-card bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-slate-400 text-xs block mb-1">Producto terminado *</label>
              <select value={form.producto_id} onChange={e => {
                const p = productos.find(x => x.id === e.target.value);
                setForm(f => ({ ...f, producto_id: p?.id || "", nombre: f.nombre || p?.nombre || "", categoria: p?.categoria || "", precio_venta_sugerido: f.precio_venta_sugerido || p?.precio_venta || 0 }));
              }} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">— Seleccionar producto —</option>
                {productos.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.sku ? `· ${p.sku}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Modo</label>
              <select value={form.modo_produccion} onChange={e => setForm(f => ({ ...f, modo_produccion: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                <option value="bajo_demanda">Bajo demanda</option>
                <option value="stock_terminado">Stock terminado</option>
              </select>
            </div>
            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><label className="text-slate-400 text-xs block mb-1">Nombre de la ficha</label>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Nombre de la ficha" /></div>
              <div><label className="text-slate-400 text-xs block mb-1">Tamaño / presentación</label>
                <input value={form.tamano} onChange={e => setForm(f => ({ ...f, tamano: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Ej: 100 ml" /></div>
              <div><label className="text-slate-400 text-xs block mb-1">Cantidad a producir (rinde) *</label>
                <input type="number" min={0} step="any" value={form.rendimiento ?? ""} onChange={e => setForm(f => ({ ...f, rendimiento: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Unidades que rinde esta receta" /></div>
              <div><label className="text-slate-400 text-xs block mb-1">Minutos de preparación</label>
                <input type="number" min={0} value={form.tiempo_preparacion_min} onChange={e => setForm(f => ({ ...f, tiempo_preparacion_min: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" placeholder="Minutos preparación" /></div>
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-700 rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 text-slate-300">
                <tr>
                  <th className="text-left p-2">Ingrediente</th>
                  <th className="text-left p-2">Unidad</th>
                  <th className="text-right p-2">Precio unitario</th>
                  <th className="text-right p-2">Cant. usada (receta completa)</th>
                  <th className="text-right p-2">Factor</th>
                  <th className="text-right p-2">Costo</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="produccion-config-list divide-y divide-slate-700">
                {ingredientesCalc.map((ing, idx) => (
                  <tr key={idx} className="bg-slate-800/60">
                    <td className="p-2 min-w-[260px]">
                      <select value={ing.producto_id || ""} onChange={e => seleccionarIngrediente(idx, e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white">
                        <option value="">— Producto del catálogo —</option>
                        {productos.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.sku ? `· ${p.sku}` : ""}</option>)}
                      </select>
                    </td>
                    <td className="p-2"><input value={ing.unidad_medida || ""} onChange={e => setIng(idx, "unidad_medida", e.target.value)} className="w-24 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white" /></td>
                    <td className="p-2"><input type="number" min={0} step="any" value={ing.precio_unitario ?? ""} onChange={e => setIng(idx, "precio_unitario", e.target.value)} className="w-28 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-right" /></td>
                    <td className="p-2"><input type="number" min={0} value={ing.cantidad_usada || ""} onChange={e => setIng(idx, "cantidad_usada", e.target.value)} className="w-24 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-right" /></td>
                    <td className="p-2"><input type="number" min={0} step="0.01" value={ing.factor_correccion || 1} onChange={e => setIng(idx, "factor_correccion", e.target.value)} className="w-20 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-right" /></td>
                    <td className="p-2 text-right text-emerald-300 font-semibold">{fmtGs(ing.costo_proporcional)}</td>
                    <td className="p-2 text-center"><button onClick={() => setForm(f => ({ ...f, ingredientes: f.ingredientes.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={() => setForm(f => ({ ...f, ingredientes: [...(f.ingredientes || []), { producto_id: "", unidad_medida: "unidad", precio_unitario: 0, cantidad_usada: 0, factor_correccion: 1 }] }))} className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Agregar ingrediente
          </button>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-slate-900/50 rounded-lg p-3"><div className="text-slate-400 text-xs">Costo de la receta completa</div><div className="text-white font-bold">{fmtGs(costoLote)}</div><div className="text-slate-500 text-xs mt-1">Rinde {rendimiento} unid. · {fmtGs(costoFinal)} c/u</div></div>
            <div className="bg-slate-900/50 rounded-lg p-3"><label className="text-slate-400 text-xs">Desperdicio %</label><input type="number" min={0} value={form.desperdicio_pct} onChange={e => setForm(f => ({ ...f, desperdicio_pct: e.target.value }))} className="mt-1 w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white" /></div>
            <div className="bg-slate-900/50 rounded-lg p-3"><label className="text-slate-400 text-xs">Precio venta sugerido</label><input type="number" min={0} value={form.precio_venta_sugerido} onChange={e => setForm(f => ({ ...f, precio_venta_sugerido: e.target.value }))} className="mt-1 w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white" /></div>
            <div className="bg-emerald-900/30 border border-emerald-700/60 rounded-lg p-3"><div className="text-emerald-200 text-xs">CMV por unidad / Margen</div><div className="text-white font-bold">{fmtGs(costoFinal)} · {margen.toFixed(1)}%</div></div>
          </div>

          <textarea value={form.instrucciones} onChange={e => setForm(f => ({ ...f, instrucciones: e.target.value }))} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" rows={3} placeholder="Ficha operativa: armado, horno, porciones, observaciones de cocina..." />
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="px-3 py-2 text-slate-400 hover:text-white text-sm">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar ficha
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {fichas.map(f => (
            <div key={f.id} className="produccion-config-card bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-white font-semibold">{f.nombre}</div>
                <div className="text-slate-400 text-xs">{f.producto_nombre} · {f.modo_produccion === "stock_terminado" ? "Stock terminado" : "Bajo demanda"}</div>
              </div>
              <span className="text-emerald-300 text-xs font-semibold">{f.margen_bruto_pct || 0}% margen</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-slate-900/50 rounded p-2"><div className="text-slate-500">CMV</div><div className="text-white font-semibold">{fmtGs(f.costo_final)}</div></div>
              <div className="bg-slate-900/50 rounded p-2"><div className="text-slate-500">Venta</div><div className="text-white font-semibold">{fmtGs(f.precio_venta_sugerido)}</div></div>
              <div className="bg-slate-900/50 rounded p-2"><div className="text-slate-500">Insumos</div><div className="text-white font-semibold">{(f.ingredientes || []).length}</div></div>
            </div>
            <div className="flex justify-end gap-2">
              {f.modo_produccion === "stock_terminado" && (
                <button onClick={() => producir(f)} className="px-2 py-1 rounded bg-emerald-600/80 hover:bg-emerald-500 text-white text-xs">Producir</button>
              )}
              <button onClick={() => editar(f)} className="p-1 text-slate-400 hover:text-indigo-300"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => eliminar(f.id)} className="p-1 text-slate-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {fichas.length === 0 && !form && (
          <div className="text-slate-500 text-sm border border-dashed border-slate-700 rounded-xl p-8 text-center lg:col-span-2">Sin fichas técnicas cargadas.</div>
        )}
      </div>
    </div>
  );
}

function TabConfig({ logo, token, productos, hasPermission }) {
  const [subTab, setSubTab] = useState("categorias");

  const subTabs = useMemo(() => [
    { id: "modo",        label: "Modo",        icon: <Factory className="w-4 h-4" />, permiso: ["produccion_configuracion.modos", "produccion.configuracion"] },
    { id: "fichas",      label: "Fichas técnicas", icon: <ClipboardList className="w-4 h-4" />, permiso: ["produccion_fichas.ver", "produccion.configuracion"] },
    { id: "categorias",  label: "Categorías",  icon: <Layers className="w-4 h-4" />, permiso: ["produccion_configuracion.categorias", "produccion_configuracion.catalogos", "produccion.configuracion"] },
    { id: "variantes",   label: "Variantes",   icon: <Tag className="w-4 h-4" />, permiso: ["produccion_configuracion.variantes", "produccion_configuracion.catalogos", "produccion.configuracion"] },
    { id: "adicionales", label: "Adicionales", icon: <Package className="w-4 h-4" />, permiso: ["produccion_configuracion.adicionales", "produccion_configuracion.catalogos", "produccion.configuracion"] },
    { id: "delivery",    label: "Delivery",    icon: <Truck className="w-4 h-4" />, permiso: ["produccion_configuracion.delivery", "produccion.configuracion"] },
  ].filter(t => t.permiso.some(p => hasPermission(p))), [hasPermission]);

  useEffect(() => {
    if (subTabs.length && !subTabs.some(t => t.id === subTab)) setSubTab(subTabs[0].id);
  }, [subTabs, subTab]);

  if (!subTabs.length) {
    return <div className="text-slate-500 text-sm border border-slate-700 rounded-xl p-6">Sin permisos para configurar Producción.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="produccion-sub-tabs flex gap-1 bg-slate-800 rounded-lg p-1 w-fit">
        {subTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`produccion-sub-tab flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              ${subTab === t.id ? "produccion-sub-tab--active bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {subTab === "modo" && (
        <ConfigModoProduccion logo={logo} token={token} />
      )}

      {subTab === "fichas" && (
        <FichasTecnicas logo={logo} token={token} productos={productos} />
      )}

      {subTab === "categorias" && (
        <SeccionCatalogo
          titulo="Categorías de producto (ej: tamaños, formatos, tipos)"
          icono={<Layers className="w-4 h-4 text-indigo-400" />}
          logo={logo} token={token} coleccion="categorias" conPrecio={false}
        />
      )}

      {subTab === "variantes" && (
        <SeccionCatalogo
          titulo="Variantes (ej: sabores, opciones, presentaciones)"
          icono={<Tag className="w-4 h-4 text-amber-400" />}
          logo={logo} token={token} coleccion="variantes" conPrecio={true}
        />
      )}

      {subTab === "adicionales" && (
        <SeccionCatalogo
          titulo="Adicionales con precio extra"
          icono={<Package className="w-4 h-4 text-emerald-400" />}
          logo={logo} token={token} coleccion="adicionales" conPrecio={true}
        />
      )}

      {subTab === "delivery" && (
        <ZonasDelivery logo={logo} token={token} />
      )}
    </div>
  );
}

// ── Zonas de delivery ─────────────────────────────────────────────────────────
function ZonasDelivery({ logo, token }) {
  const [zonas, setZonas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]     = useState({ id: "", nombre: "", descripcion: "", costo_delivery: 0 });
  const [editando, setEditando] = useState(false);
  const [saving, setSaving] = useState(false);
  const hdr = { Authorization: `Bearer ${token}` };
  const endpoint = `${API}/admin/produccion/zonas-delivery`;

  const cargar = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${endpoint}?logo_tipo=${logo}`, { headers: hdr });
    if (r.ok) setZonas(await r.json());
    setLoading(false);
  }, [logo, token]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("Ingrese un nombre de zona"); return; }
    setSaving(true);
    const id = form.id || slugId(form.nombre) + "_" + Date.now();
    const r = await fetch(`${endpoint}/${id}?logo_tipo=${logo}`, {
      method: "PUT",
      headers: { ...hdr, "Content-Type": "application/json" },
      body: JSON.stringify({ nombre: form.nombre.trim(), descripcion: form.descripcion.trim(), costo_delivery: Number(form.costo_delivery) }),
    });
    setSaving(false);
    if (r.ok) { toast.success("Zona guardada"); setForm({ id: "", nombre: "", descripcion: "", costo_delivery: 0 }); setEditando(false); cargar(); }
    else toast.error("Error al guardar");
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta zona?")) return;
    await fetch(`${endpoint}/${id}?logo_tipo=${logo}`, { method: "DELETE", headers: hdr });
    cargar();
  };

  return (
    <div className="produccion-config-card bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="produccion-config-title text-white font-medium flex items-center gap-2"><Truck className="w-4 h-4 text-blue-400" /> Zonas de Delivery</h3>
        <button onClick={() => { setForm({ id: "", nombre: "", descripcion: "", costo_delivery: 0 }); setEditando(true); }}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Agregar zona
        </button>
      </div>

      {editando && (
        <div className="produccion-config-form bg-slate-700/50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Nombre de zona *</label>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm" placeholder="Ej: Centro" />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Descripción</label>
              <input value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm" placeholder="Barrio, referencia..." />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Costo delivery (Gs.)</label>
              <input type="number" min={0} value={form.costo_delivery}
                onChange={e => setForm(f => ({ ...f, costo_delivery: e.target.value }))}
                className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditando(false)} className="text-slate-400 text-xs hover:text-white">Cancelar</button>
            <button onClick={guardar} disabled={saving}
              className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-xs flex items-center gap-1">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-500 text-sm text-center py-3">Cargando...</div> : zonas.length === 0
        ? <div className="text-slate-400 text-sm text-center py-3">Sin zonas configuradas</div>
        : (
          <div className="produccion-config-list divide-y divide-slate-700">
            {zonas.map(z => (
              <div key={z.id} className="produccion-config-row flex items-center justify-between py-2">
                <div>
                  <span className="text-white text-sm font-medium">{z.nombre}</span>
                  {z.descripcion && <span className="text-slate-400 text-xs ml-2">{z.descripcion}</span>}
                  <span className="text-blue-400 text-xs ml-2">{fmtGs(z.costo_delivery)}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setForm({ id: z.id, nombre: z.nombre, descripcion: z.descripcion || "", costo_delivery: z.costo_delivery || 0 }); setEditando(true); }}
                    className="p-1 text-slate-400 hover:text-indigo-400"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => eliminar(z.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── Tab Historial ─────────────────────────────────────────────────────────────
const hoyProd = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const prevDiaProd = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() - 1); return dt.toISOString().slice(0, 10); };
const nextDiaProd = (d) => { const dt = new Date(d + "T12:00:00"); dt.setDate(dt.getDate() + 1); return dt.toISOString().slice(0, 10); };
const diaLabelProd = (d) => { if (!d) return ""; return new Date(d + "T12:00:00").toLocaleDateString("es-PY", { weekday: "short", day: "numeric", month: "short" }); };

function TabHistorial({ logo, token }) {
  const [dia, setDia]       = useState(hoyProd);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const hdr = { Authorization: `Bearer ${token}` };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/admin/produccion/ordenes?logo_tipo=${logo}&solo_activas=false&desde=${dia}&hasta=${dia}`,
        { headers: hdr }
      );
      if (r.ok) setOrdenes(await r.json());
    } finally { setLoading(false); }
  }, [logo, token, dia]);

  useEffect(() => { cargar(); }, [cargar]);

  const resumen = useMemo(() => {
    const base = Object.fromEntries(KDS_ESTADOS.map(e => [e, { count: 0, total: 0 }]));
    ordenes.forEach(o => {
      const estado = o.estado_kds || "A RECIBIR";
      if (!base[estado]) base[estado] = { count: 0, total: 0 };
      base[estado].count += 1;
      base[estado].total += Number(o.totales?.total_general || 0);
    });
    return base;
  }, [ordenes]);

  return (
    <div className="space-y-4">
      {/* Filtro de día */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="produccion-history-filter flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-1 py-0.5">
          <button onClick={() => setDia(prevDiaProd(dia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-white text-xs font-medium min-w-[110px] text-center px-1 capitalize">{diaLabelProd(dia)}</span>
          <button onClick={() => setDia(nextDiaProd(dia))} className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded transition-all">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {dia !== hoyProd() && (
          <button onClick={() => setDia(hoyProd())} className="text-xs text-arandu-blue hover:text-blue-300 transition-colors">Hoy</button>
        )}
        <span className="text-slate-500 text-xs">{loading ? "Cargando..." : `${ordenes.length} orden${ordenes.length !== 1 ? "es" : ""}`}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KDS_ESTADOS.map(estado => {
          const color = KDS_COLOR[estado];
          const data = resumen[estado] || { count: 0, total: 0 };
          return (
            <div key={estado} className={`produccion-history-stat-card produccion-kds-column--${KDS_CLASS[estado]} rounded-xl border border-white/10 p-3 ${color.bg}`}>
              <div className={`produccion-status-badge produccion-status-badge--${KDS_CLASS[estado]} inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color.badge}`}>
                {color.icon} {estado}
              </div>
              <div className="text-white text-2xl font-bold mt-2">{data.count}</div>
              <div className="text-slate-400 text-xs">{fmtGs(data.total)}</div>
            </div>
          );
        })}
      </div>

      {/* Lista de órdenes */}
      {!loading && ordenes.length === 0 ? (
        <div className="text-center text-slate-400 py-12">Sin órdenes para este día</div>
      ) : (
        <div className="space-y-2">
          {ordenes.map(o => (
            <div key={o.id} className="produccion-history-card bg-slate-800 border border-slate-700 rounded-xl p-4 text-sm">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <span className="text-slate-400 text-xs">#{o.nro_orden}</span>
                  <div className="text-white font-semibold">{o.cliente?.nombre || "—"}</div>
                  {o.cliente?.telefono && <div className="text-slate-400 text-xs">{o.cliente.telefono}</div>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.facturado ? "bg-emerald-600/30 text-emerald-300" : "bg-amber-600/30 text-amber-300"}`}>
                    {o.facturado ? "Facturado" : o.estado_kds}
                  </span>
                  <span className="text-xs text-slate-500">{o.modalidad}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="produccion-history-item-pill bg-slate-700/50 text-slate-300 text-xs px-2 py-1 rounded">
                    <span className="font-semibold text-white">{it.cantidad}× {it.nombre_producto}</span>
                    {it.variantes_texto && <span className="ml-1 text-slate-400">{it.variantes_texto}</span>}
                    {it.adicionales_texto && <span className="ml-1 text-emerald-300">+ {it.adicionales_texto}</span>}
                    {it.ficha_tecnica_id && <span className="ml-1 text-indigo-300">Ficha técnica</span>}
                    <span className="ml-2 text-emerald-300">{fmtGs(it.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{o.fecha_creacion ? new Date(o.fecha_creacion).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                <span className="text-emerald-400 font-bold">{fmtGs(o.totales?.total_general)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ProduccionPage() {
  const navigate = useNavigate();
  const { user, token, hasPermission, activeEmpresaPropia, empresasPropias } = useContext(AuthContext);
  const [tab, setTab] = useState("kds");
  const [empresas, setEmpresas]   = useState([]);
  const [clientesCatastro, setClientesCatastro] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cuentasDisp, setCuentasDisp] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);

  const logo = activeEmpresaPropia?.slug || (user?.logos_asignados?.[0]) || "arandujar";
  const hdr  = { Authorization: `Bearer ${token}` };

  const normalizarClienteFactura = useCallback((cliente) => ({
    ...cliente,
    id: cliente.id,
    empresa_id: cliente.id,
    nombre: cliente.nombre || cliente.razon_social || "",
    razon_social: cliente.razon_social || cliente.nombre || "",
    logo_tipo: cliente.logo_tipo || logo,
  }), [logo]);

  const cargarClientesCatastro = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/entidades?es_cliente=true&activo=true&logo_tipo=${logo}`, { headers: hdr });
      const data = r.ok ? await r.json() : [];
      const clientes = Array.isArray(data) ? data : [];
      setClientesCatastro(clientes);
      setEmpresas(clientes.map(normalizarClienteFactura));
    } catch {
      setClientesCatastro([]);
      setEmpresas([]);
    }
  }, [logo, token, normalizarClienteFactura]); // eslint-disable-line

  useEffect(() => {
    // Clientes del Catastro unificado y productos disponibles para producción/facturación.
    cargarClientesCatastro();
    Promise.all([
      fetch(`${API}/admin/produccion/catalogos/productos?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/cuentas-bancarias?logo_tipo=${logo}`, { headers: hdr }),
      fetch(`${API}/admin/plan-cuentas?logo_tipo=${logo}`, { headers: hdr }),
    ]).then(async ([rProd, rCuentas, rPlan]) => {
      if (rProd.ok) {
        const d = await rProd.json();
        setProductos(Array.isArray(d) ? d : d.items || []);
      }
      if (rCuentas.ok) setCuentasDisp(await rCuentas.json());
      if (rPlan.ok) setPlanCuentas(await rPlan.json());
    }).catch(() => {});
  }, [logo, token, cargarClientesCatastro]); // eslint-disable-line

  if (!hasPermission("produccion.ver")) {
    return (
      <div className="min-h-screen bg-arandu-dark flex items-center justify-center">
        <div className="text-center text-slate-400">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
          <p>Sin acceso al módulo de Producción.</p>
          <button onClick={() => navigate("/sistema")} className="mt-4 text-indigo-400 hover:text-indigo-300 text-sm">Volver al inicio</button>
        </div>
      </div>
    );
  }

  const esTemaClaroPage = String(activeEmpresaPropia?.tema || "").startsWith("claro");

  return (
    <div className={`produccion-page ${esTemaClaroPage ? "produccion-page--claro" : ""} min-h-screen bg-arandu-dark`}>
      <div className="max-w-screen-xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/sistema")} className="produccion-back-btn text-slate-400 hover:text-white transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="produccion-page-title text-white font-heading text-2xl font-bold flex items-center gap-2">
              <ChefHat className="w-6 h-6 text-indigo-400" /> Producción
            </h1>
            <p className="produccion-page-subtitle text-slate-400 text-sm">
              {activeEmpresaPropia?.nombre || logo} — KDS, comandas y configuración de catálogos
            </p>
          </div>
        </div>

        {/* Tabs principales */}
        <div className="produccion-main-tabs flex gap-1 bg-slate-800/60 border border-slate-700 rounded-xl p-1 w-fit">
          <button
            onClick={() => setTab("kds")}
            className={`produccion-main-tab flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${tab === "kds" ? "produccion-main-tab--active bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
          >
            <ClipboardList className="w-4 h-4" /> Cocina (KDS)
          </button>
          <button
            onClick={() => setTab("historial")}
            className={`produccion-main-tab flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${tab === "historial" ? "produccion-main-tab--active bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
          >
            <Receipt className="w-4 h-4" /> Historial
          </button>
          {(canProd(hasPermission, "produccion_configuracion.ver", "produccion.configuracion") || hasPermission("produccion_fichas.ver")) && (
            <button
              onClick={() => setTab("config")}
              className={`produccion-main-tab flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${tab === "config" ? "produccion-main-tab--active bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              <Settings className="w-4 h-4" /> Configuración
            </button>
          )}
        </div>

        {/* Contenido */}
        {tab === "kds" && (
          <TabKDS
            logo={logo}
            token={token}
            hasPermission={hasPermission}
            empresas={empresas}
            activeEmpresaPropia={activeEmpresaPropia}
            productos={productos}
            clientesCatastro={clientesCatastro}
            cuentasDisp={cuentasDisp}
            planCuentas={planCuentas}
            onClienteCreado={cliente => {
              setClientesCatastro(prev => [...prev.filter(c => c.id !== cliente.id), cliente].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
              setEmpresas(prev => [...prev.filter(c => c.id !== cliente.id), normalizarClienteFactura(cliente)].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
            }}
          />
        )}
        {tab === "historial" && (
          <TabHistorial logo={logo} token={token} />
        )}
        {tab === "config" && (canProd(hasPermission, "produccion_configuracion.ver", "produccion.configuracion") || hasPermission("produccion_fichas.ver")) && (
          <TabConfig logo={logo} token={token} productos={productos} hasPermission={hasPermission} />
        )}
      </div>
    </div>
  );
}
